import { CSSProperties, ReactNode, useEffect, useMemo, useState } from "react";
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  Row,
  SortingState,
  PaginationState,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  useReactTable,
} from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FilterX } from "lucide-react";
import { SearchInput } from "@/components/atoms/search";
import { DataTable } from "@/components/organisms/DataTable";
import { TableFooter, FilterBar } from "@/components/molecules/table";

// ─── Shared filter function ────────────────────────────────────────────────────

/**
 * OR-based faceted filter: filterValue is string[], cell value is a scalar.
 * Row passes if its string-coerced cell value is in the selected set.
 *
 * Export and use as `filterFn` in ColumnDef for any filterable column.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const selectedIncludesFn: FilterFn<any> = (
  row: Row<unknown>,
  columnId: string,
  filterValue: string[]
) => {
  if (!filterValue?.length) return true;
  return filterValue.includes(String(row.getValue(columnId) ?? ""));
};
selectedIncludesFn.autoRemove = (val: string[]) => !val?.length;

// ─── Component ────────────────────────────────────────────────────────────────

const DEFAULT_PAGE_SIZE_OPTIONS = [20, 50, 100];

interface SelfContainedDataTableProps<TData> {
  data: TData[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<TData, any>[];
  isLoading?: boolean;
  /**
   * Returns the string to search against for a given row.
   * Tokens from the global search box are matched against this string.
   */
  getSearchableText: (row: TData) => string;
  /** External filter panel (e.g. date range, territory selects). Rendered above the toolbar. */
  filterPanel?: ReactNode;
  /** Extra actions rendered in the toolbar (e.g. column visibility dropdown, export button). */
  toolbarActions?: ReactNode;
  /**
   * Called whenever the fully-filtered row set changes (after text search + column filters).
   * Use this to drive exports that respect the current table state.
   * `isFiltered` is true when any filter is active.
   */
  onFilteredDataChange?: (rows: TData[], isFiltered: boolean) => void;
  onRowClick?: (row: TData) => void;
  getRowClassName?: (row: TData) => string;
  getRowStyle?: (row: TData) => CSSProperties;
  emptyMessage?: string;
  searchPlaceholder?: string;
  defaultPageSize?: number;
  pageSizeOptions?: number[];
  initialSorting?: SortingState;
}

/**
 * Self-contained table: owns global search, faceted column filters, sorting,
 * and pagination internally. Accepts pre-fetched/pre-filtered data via `data`.
 *
 * Pair with DataColumnHeader for per-column sort + faceted filter UI.
 */
export function SelfContainedDataTable<TData>({
  data,
  columns,
  isLoading = false,
  getSearchableText,
  filterPanel,
  toolbarActions,
  onRowClick,
  getRowClassName,
  getRowStyle,
  emptyMessage = "No se encontraron registros",
  searchPlaceholder = "Buscar...",
  defaultPageSize = 20,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  onFilteredDataChange,
  initialSorting,
}: SelfContainedDataTableProps<TData>) {
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>(initialSorting ?? []);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });

  // Pre-filter by global text search before handing data to TanStack.
  // This lets getSearchableText access fields not exposed as columns.
  const textFiltered = useMemo(() => {
    if (!globalFilter.trim()) return data;
    const tokens = globalFilter.trim().toLowerCase().split(/\s+/);
    return data.filter((row) => {
      const text = getSearchableText(row).toLowerCase();
      return tokens.every((t) => text.includes(t));
    });
  }, [data, globalFilter, getSearchableText]);

  const table = useReactTable({
    data: textFiltered,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    state: { columnFilters, sorting, pagination },
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    autoResetPageIndex: true,
  });

  const activeFilterCount =
    columnFilters.length + (globalFilter.trim() ? 1 : 0);

  // Notify parent of current filtered dataset (for exports).
  // Deps: textFiltered covers global-search changes; columnFilters covers facet changes.
  useEffect(() => {
    if (!onFilteredDataChange) return;
    const rows = table.getFilteredRowModel().rows.map((r) => r.original);
    onFilteredDataChange(rows, activeFilterCount > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textFiltered, columnFilters, onFilteredDataChange]);

  const clearAllFilters = () => {
    setColumnFilters([]);
    setGlobalFilter("");
    table.resetColumnFilters();
  };

  const totalFiltered = table.getFilteredRowModel().rows.length;

  return (
    <div className="w-full space-y-0">
      <div className="rounded-md border">
        {filterPanel && <FilterBar>{filterPanel}</FilterBar>}

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 p-4">
          <SearchInput
            value={globalFilter}
            onChange={setGlobalFilter}
            placeholder={searchPlaceholder}
            disabled={isLoading}
            className="max-w-xs flex-1"
          />

          {toolbarActions}

          {activeFilterCount > 0 && (
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-xs">
                {activeFilterCount} filtro{activeFilterCount > 1 ? "s" : ""} activo
                {activeFilterCount > 1 ? "s" : ""}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={clearAllFilters}
                className="h-8 gap-1.5 text-xs"
              >
                <FilterX className="size-3.5" />
                Limpiar
              </Button>
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
            Cargando datos...
          </div>
        ) : (
          <DataTable
            table={table}
            onRowClick={onRowClick}
            getRowClassName={getRowClassName}
            getRowStyle={getRowStyle}
            emptyMessage={
              activeFilterCount > 0
                ? "Ningún registro coincide con los filtros aplicados"
                : emptyMessage
            }
          />
        )}

        <TableFooter
          currentPage={table.getState().pagination.pageIndex + 1}
          totalPages={table.getPageCount()}
          totalItems={totalFiltered}
          pageSize={table.getState().pagination.pageSize}
          onPageChange={(page) => table.setPageIndex(page - 1)}
          onPageSizeChange={(size) => {
            table.setPageSize(size);
            table.setPageIndex(0);
          }}
          pageSizeOptions={pageSizeOptions}
        />
      </div>
    </div>
  );
}
